// src/pages/VoterDetails.page.tsx
import { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { supabase } from "../lib/supabase";

/** Types (loose so the view can vary safely) */
type ViewRow = {
  campaign_voter_id: string;
  campaign_id: string | null;
  voter_id: string | null;

  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  suffix: string | null;

  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;

  // the view may or may not include any of these:
  phone?: string | null;
  home_phone?: string | null;
  cell_phone?: string | null;
  email?: string | null;
  facebook?: string | null;
  x_twitter?: string | null;
  twitter?: string | null;
  instagram?: string | null;
  tiktok?: string | null;
  support_level?: string | null;
};

type CampaignVoterRow = {
  id: string;
  campaign_id: string | null;
  voter_id: string;

  support_level: string | null;

  home_phone_override: string | null;
  cell_phone_override: string | null;
  email_override: string | null;

  social_facebook_override: string | null;
  social_x_override: string | null;
  social_instagram_override: string | null;
  social_tiktok_override: string | null;

  contact_last_verified_at: string | null;
  contact_source: string | null;

  // plus many other fields we don't need here
  [k: string]: any;
};

type NoteRow = { id: string; created_at: string; note_type: string | null; note: string; method: string | null; outcome: string | null };
type ContactRow = { id: string; contacted_at: string; contact_method: string | null; contact_type: string | null; result: string | null; notes: string | null };
type HistoryRow = { id: string; election_name: string | null; election_date: string | null };
type RelType = { id: string; label: string; key: string; category: string; sort_order: number; is_active: boolean };
type RelationshipRow = { id: string; from_id: string | null; to_id: string | null; rel_type_id: string; created_at: string; notes: string | null };

const SENTIMENTS = [
  { key: "Strong", color: "#22c55e" },
  { key: "Lean", color: "#86efac" },
  { key: "Undecided", color: "#facc15" },
  { key: "Lean Against", color: "#f97316" },
  { key: "Strong Against", color: "#ef4444" },
] as const;

const BTN: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  background: "#0B2E5B",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
};
const BTN_LIGHT: React.CSSProperties = { ...BTN, background: "#fff", color: "#0B2E5B", border: "1px solid #9ec5ff" };

const th: React.CSSProperties = { textAlign: "left", padding: 8, borderBottom: "1px solid #eee" };
const td: React.CSSProperties = { padding: 8, borderBottom: "1px solid #f3f4f6", verticalAlign: "top" };

function fullName(v?: Partial<ViewRow> | null) {
  if (!v) return "";
  return [v.first_name, v.middle_name, v.last_name, v.suffix].filter(Boolean).join(" ").trim();
}
const fmtWhen = (iso?: string) =>
  iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

/** fallback helper */
function pick(o: any, ...names: string[]): string | null {
  for (const n of names) {
    const v = o?.[n];
    if (v != null && String(v).trim() !== "") return String(v);
  }
  return null;
}

export default function VoterDetail() {
  const { id: rawId } = useParams<{ id: string }>();
  const qs = new URLSearchParams(useLocation().search);
  const campaignFromUrl = qs.get("campaign");

  const [cvId, setCvId] = useState<string | null>(null);

  const [viewRow, setViewRow] = useState<ViewRow | null>(null);
  const [cvRow, setCvRow] = useState<CampaignVoterRow | null>(null);

  const [notes, setNotes] = useState<NoteRow[]>([]);
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [relTypes, setRelTypes] = useState<RelType[]>([]);
  const [rels, setRels] = useState<Array<{ id: string; person: string; when: string; rel: string; note: string | null }>>([]);

  const [showEdit, setShowEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // NEW: inline add modals + form state
  const [showAddNote, setShowAddNote] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showAddRel, setShowAddRel] = useState(false);

  const [noteForm, setNoteForm] = useState({ note_type: "", note: "" });
  const [contactForm, setContactForm] = useState({
    contacted_at: new Date().toISOString().slice(0, 16), // YYYY-MM-DDTHH:mm for <input type="datetime-local">
    contact_method: "",
    contact_type: "",
    result: "",
    notes: "",
  });
  const [relForm, setRelForm] = useState({
    rel_type_id: "",
    to_id: "",
    notes: "",
  });

  /** Resolve campaign_voter_id (route may have voter_id) */
  useEffect(() => {
    (async () => {
      if (!rawId) return;
      setErr(null);
      setCvId(null);

      const looksUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(rawId);
      if (looksUuid) {
        setCvId(rawId);
        return;
      }
      if (!campaignFromUrl) {
        setErr("Missing campaign id in URL (?campaign=...)");
        return;
      }
      const { data, error } = await supabase
        .from("campaign_voters_with_address")
        .select("campaign_voter_id")
        .eq("campaign_id", campaignFromUrl)
        .eq("voter_id", rawId)
        .maybeSingle();
      if (error) { setErr(error.message); return; }
      if (!data?.campaign_voter_id) { setErr("Voter not found in this campaign."); return; }
      setCvId(data.campaign_voter_id);
    })();
  }, [rawId, campaignFromUrl]);

  /** Load rows + lists */
  useEffect(() => {
    if (!cvId) return;

    (async () => {
      setErr(null);

      const view = await supabase
        .from("campaign_voters_with_address")
        .select("*")
        .eq("campaign_voter_id", cvId)
        .single();
      if (view.error) { setErr(view.error.message); return; }
      const v = (view.data ?? null) as ViewRow | null;
      setViewRow(v);

      const cv = await supabase
        .from("campaign_voters")
        .select("*")
        .eq("id", cvId)
        .single();
      if (!cv.error) setCvRow(cv.data as CampaignVoterRow);

      const campaign_id = v?.campaign_id ?? cv.data?.campaign_id ?? null;
      if (campaign_id) {
        const [nRes, cRes, hRes, rTypes] = await Promise.all([
          supabase.from("voter_notes").select("id,created_at,note_type,note,method,outcome").eq("campaign_id", campaign_id).eq("voter_id", cvId).order("created_at", { ascending: false }),
          supabase.from("voter_contacts").select("id,contacted_at,contact_method,contact_type,result,notes").eq("campaign_id", campaign_id).eq("voter_id", cvId).order("contacted_at", { ascending: false }),
          supabase.from("campaign_voter_history").select("id,election_name,election_date").eq("campaign_id", campaign_id).eq("voter_id", cvId).order("election_date", { ascending: false }),
          supabase.from("campaign_relationship_types").select("id,label,key,category,sort_order,is_active").eq("campaign_id", campaign_id).eq("is_active", true).order("sort_order"),
        ]);
        if (!nRes.error) setNotes((nRes.data ?? []) as NoteRow[]);
        if (!cRes.error) setContacts((cRes.data ?? []) as ContactRow[]);
        if (!hRes.error) setHistory((hRes.data ?? []) as HistoryRow[]);
        if (!rTypes.error) setRelTypes((rTypes.data ?? []) as RelType[]);

        const relRows = await supabase
          .from("relationships")
          .select("id,from_id,to_id,rel_type_id,created_at,notes")
          .eq("campaign_id", campaign_id)
          .or(`from_id.eq.${cvId},to_id.eq.${cvId}`);
        if (!relRows.error) {
          const rows = (relRows.data ?? []) as RelationshipRow[];
          const otherIds = Array.from(new Set(rows.map(r => (r.from_id === cvId ? r.to_id : r.from_id)!).filter(Boolean)));
          const others = otherIds.length
            ? await supabase.from("campaign_voters_with_address").select("campaign_voter_id, first_name, middle_name, last_name, suffix").in("campaign_voter_id", otherIds)
            : { data: [] as any[] };
          const nameById = new Map<string, string>();
          (others.data ?? []).forEach((p: any) => nameById.set(p.campaign_voter_id, fullName(p)));
          const labelById = new Map(relTypes.map(rt => [rt.id, rt.label]));
          setRels(rows.map(r => ({
            id: r.id,
            person: nameById.get(r.from_id === cvId ? (r.to_id as string) : (r.from_id as string)) ?? "(unknown)",
            rel: labelById.get(r.rel_type_id) ?? "",
            when: fmtWhen(r.created_at),
            note: r.notes ?? null,
          })));
        }
      }
    })();
  }, [cvId]);

  /** Merge for display: prefer overrides from campaign_voters, else view */
  const display = useMemo(() => {
    const override: Partial<CampaignVoterRow> = cvRow ?? {};
    const base: Partial<ViewRow> = viewRow ?? {};
    return {
      home: override.home_phone_override ?? pick(base, "home_phone", "phone") ?? null,
      cell: override.cell_phone_override ?? pick(base, "cell_phone") ?? null,
      email: override.email_override ?? pick(base, "email") ?? null,
      facebook: override.social_facebook_override ?? pick(base, "facebook") ?? null,
      x: override.social_x_override ?? pick(base, "x_twitter", "twitter") ?? null,
      instagram: override.social_instagram_override ?? pick(base, "instagram") ?? null,
      tiktok: override.social_tiktok_override ?? pick(base, "tiktok") ?? null,
      support: override.support_level ?? base.support_level ?? "Undecided",
    };
  }, [cvRow, viewRow]);

  /** Sentiment update */
  async function updateSupport(newLevel: string) {
    if (!cvId) return;
    const { error } = await supabase.from("campaign_voters").update({ support_level: newLevel }).eq("id", cvId);
    if (error) { alert(error.message); return; }
    setCvRow(prev => ({ ...(prev ?? ({} as CampaignVoterRow)), support_level: newLevel }));
  }

  /** Edit Contact Info modal state */
  const [form, setForm] = useState({
    home: "", cell: "", email: "",
    facebook: "", x: "", instagram: "", tiktok: "",
  });

  useEffect(() => {
    setForm({
      home: display.home ?? "",
      cell: display.cell ?? "",
      email: display.email ?? "",
      facebook: display.facebook ?? "",
      x: display.x ?? "",
      instagram: display.instagram ?? "",
      tiktok: display.tiktok ?? "",
    });
  }, [display.home, display.cell, display.email, display.facebook, display.x, display.instagram, display.tiktok]);

  async function saveContactInfo() {
    if (!cvId) return;
    setSaving(true);
    setErr(null);
    setMsg(null);
    try {
      const payload = {
        home_phone_override: form.home.trim() || null,
        cell_phone_override: form.cell.trim() || null,
        email_override: form.email.trim() || null,
        social_facebook_override: form.facebook.trim() || null,
        social_x_override: form.x.trim() || null,
        social_instagram_override: form.instagram.trim() || null,
        social_tiktok_override: form.tiktok.trim() || null,
        contact_last_verified_at: new Date().toISOString(),
        contact_source: "manual",
      };
      const { error } = await supabase.from("campaign_voters").update(payload).eq("id", cvId);
      if (error) throw error;
      setShowEdit(false);
      setMsg("Saved.");
      const cv = await supabase.from("campaign_voters").select("*").eq("id", cvId).single();
      if (!cv.error) setCvRow(cv.data as CampaignVoterRow);
    } catch (e: any) {
      setErr(e?.message ?? "Could not save");
    } finally {
      setSaving(false);
    }
  }

  // ========= NEW: inline add handlers =========
  const campaign_id = viewRow?.campaign_id ?? cvRow?.campaign_id ?? null;

  async function handleAddNote() {
    if (!cvId || !campaign_id) return;
    try {
      setSaving(true);
      const payload = {
        campaign_id,
        voter_id: cvId,
        note_type: noteForm.note_type?.trim() || null,
        note: noteForm.note?.trim() || "",
      };
      const { error } = await supabase.from("voter_notes").insert(payload);
      if (error) throw error;
      const nRes = await supabase
        .from("voter_notes")
        .select("id,created_at,note_type,note,method,outcome")
        .eq("campaign_id", campaign_id)
        .eq("voter_id", cvId)
        .order("created_at", { ascending: false });
      if (!nRes.error) setNotes((nRes.data ?? []) as NoteRow[]);
      setShowAddNote(false);
      setNoteForm({ note_type: "", note: "" });
    } catch (e: any) {
      setErr(e?.message ?? "Could not add note");
    } finally {
      setSaving(false);
    }
  }

  async function handleAddContact() {
    if (!cvId || !campaign_id) return;
    try {
      setSaving(true);
      const contactedISO = contactForm.contacted_at
        ? new Date(contactForm.contacted_at).toISOString()
        : new Date().toISOString();

      const payload = {
        campaign_id,
        voter_id: cvId,
        contacted_at: contactedISO,
        contact_method: contactForm.contact_method?.trim() || null,
        contact_type: contactForm.contact_type?.trim() || null,
        result: contactForm.result?.trim() || null,
        notes: contactForm.notes?.trim() || null,
      };
      const { error } = await supabase.from("voter_contacts").insert(payload);
      if (error) throw error;
      const cRes = await supabase
        .from("voter_contacts")
        .select("id,contacted_at,contact_method,contact_type,result,notes")
        .eq("campaign_id", campaign_id)
        .eq("voter_id", cvId)
        .order("contacted_at", { ascending: false });
      if (!cRes.error) setContacts((cRes.data ?? []) as ContactRow[]);
      setShowAddContact(false);
      setContactForm({
        contacted_at: new Date().toISOString().slice(0, 16),
        contact_method: "",
        contact_type: "",
        result: "",
        notes: "",
      });
    } catch (e: any) {
      setErr(e?.message ?? "Could not add contact");
    } finally {
      setSaving(false);
    }
  }

  async function handleAddRelationship() {
    if (!cvId || !campaign_id) return;
    try {
      setSaving(true);
      const payload = {
        campaign_id,
        from_id: cvId,
        to_id: relForm.to_id?.trim() || null,
        rel_type_id: relForm.rel_type_id || null,
        notes: relForm.notes?.trim() || null,
      };
      const { error } = await supabase.from("relationships").insert(payload);
      if (error) throw error;

      const relRows = await supabase
        .from("relationships")
        .select("id,from_id,to_id,rel_type_id,created_at,notes")
        .eq("campaign_id", campaign_id)
        .or(`from_id.eq.${cvId},to_id.eq.${cvId}`);
      if (!relRows.error) {
        const rows = (relRows.data ?? []) as RelationshipRow[];
        const otherIds = Array.from(new Set(rows.map(r => (r.from_id === cvId ? r.to_id : r.from_id)!).filter(Boolean)));
        const others = otherIds.length
          ? await supabase.from("campaign_voters_with_address").select("campaign_voter_id, first_name, middle_name, last_name, suffix").in("campaign_voter_id", otherIds)
          : { data: [] as any[] };
        const nameById = new Map<string, string>();
        (others.data ?? []).forEach((p: any) => nameById.set(p.campaign_voter_id, fullName(p)));
        const labelById = new Map(relTypes.map(rt => [rt.id, rt.label]));
        setRels(rows.map(r => ({
          id: r.id,
          person: nameById.get(r.from_id === cvId ? (r.to_id as string) : (r.from_id as string)) ?? "(unknown)",
          rel: labelById.get(r.rel_type_id) ?? "",
          when: fmtWhen(r.created_at),
          note: r.notes ?? null,
        })));
      }
      setShowAddRel(false);
      setRelForm({ rel_type_id: "", to_id: "", notes: "" });
    } catch (e: any) {
      setErr(e?.message ?? "Could not add relationship");
    } finally {
      setSaving(false);
    }
  }

  if (!rawId) return <div style={{ padding: 16 }}>Missing voter id</div>;
  if (!cvId) return <div style={{ padding: 16 }}>Loading…</div>;
  if (err) return <div style={{ padding: 16, color: "#a40000" }}>Error: {err}</div>;

  const sentiment = display.support ?? "Undecided";
  const sInfo = SENTIMENTS.find((s) => s.key === sentiment);
  const headerName = fullName(viewRow);
  const addressLine1 = [viewRow?.address1, viewRow?.address2].filter(Boolean).join(" ").trim();
  const addressLine2 = [viewRow?.city, viewRow?.state, viewRow?.zip].filter(Boolean).join(", ").trim();

  return (
    <div style={{ padding: 16 }}>
      {/* Header */}
      <section style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14, marginBottom: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ width: 40, height: 40, borderRadius: 9999, background: "#0B2E5B", color: "#fff", display: "grid", placeItems: "center", fontWeight: 800 }}>
                {(viewRow?.first_name ?? " ")[0]}{(viewRow?.last_name ?? " ")[0]}
              </div>
              <div>
                <div style={{ fontSize: 20, fontWeight: 800 }}>{headerName}</div>
                <div style={{ color: "#6b7280", marginTop: 2 }}>
                  {addressLine1 || "—"}{addressLine2 ? `, ${addressLine2}` : ""}
                </div>
                <div style={{ display: "flex", gap: 16, marginTop: 6, flexWrap: "wrap" }}>
                  {display.home && <span title="Home phone">🏠 <a href={`tel:${display.home}`} style={{ color: "#0B2E5B" }}>{display.home}</a></span>}
                  {display.cell && <span title="Cell phone">📱 <a href={`tel:${display.cell}`} style={{ color: "#0B2E5B" }}>{display.cell}</a></span>}
                  {display.email && <span title="Email">✉️ <a href={`mailto:${display.email}`} style={{ color: "#0B2E5B" }}>{display.email}</a></span>}
                  {(display.facebook || display.x || display.instagram || display.tiktok) && <span>▸ Social profiles</span>}
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 999, border: "1px solid #e5e7eb", background: "#fff" }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: sInfo?.color ?? "#ddd" }} />
              <span style={{ fontWeight: 700 }}>{sentiment}</span>
              <select
                value={sentiment}
                onChange={(e) => updateSupport(e.target.value)}
                style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "4px 6px", marginLeft: 6 }}
              >
                {SENTIMENTS.map((s) => <option key={s.key} value={s.key}>{s.key}</option>)}
              </select>
            </div>

            {/* CHANGED: open inline modals instead of redirecting */}
            <button style={BTN_LIGHT} onClick={() => setShowAddContact(true)}>New Contact</button>
            <button style={BTN_LIGHT} onClick={() => setShowAddNote(true)}>New Note</button>
            <button style={BTN_LIGHT} onClick={() => setShowAddRel(true)}>New Relationship</button>
            <button style={BTN} onClick={() => setShowEdit(true)}>Edit Contact Info</button>
          </div>
        </div>
      </section>

      {/* Contacts */}
      <section style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 12, margin: "12px 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Contacts</div>
          <button style={BTN_LIGHT} onClick={() => setShowAddContact(true)}>+ Add</button>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={th}>When</th><th style={th}>Method</th><th style={th}>Outcome</th><th style={th}>Notes</th></tr></thead>
          <tbody>
            {contacts.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: 8, color: "#777" }}>No contacts yet</td></tr>
            ) : contacts.map((c) => (
              <tr key={c.id}>
                <td style={td}>{fmtWhen(c.contacted_at)}</td>
                <td style={td}>{c.contact_method ?? ""}</td>
                <td style={td}>{c.result ?? ""}</td>
                <td style={td}>{c.notes ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Notes */}
      <section style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 12, margin: "12px 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Notes</div>
          <button style={BTN_LIGHT} onClick={() => setShowAddNote(true)}>+ Add</button>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={th}>When</th><th style={th}>Type</th><th style={th}>Note</th></tr></thead>
          <tbody>
            {notes.length === 0 ? (
              <tr><td colSpan={3} style={{ padding: 8, color: "#777" }}>No notes yet</td></tr>
            ) : notes.map((n) => (
              <tr key={n.id}>
                <td style={td}>{fmtWhen(n.created_at)}</td>
                <td style={td}>{n.note_type ?? ""}</td>
                <td style={td}>{n.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* History */}
      <section style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 12, margin: "12px 0" }}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Voter History</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={th}>Election</th><th style={th}>Date</th></tr></thead>
          <tbody>
            {history.length === 0 ? (
              <tr><td colSpan={2} style={{ padding: 8, color: "#777" }}>No history</td></tr>
            ) : history.map((h) => (
              <tr key={h.id}>
                <td style={td}>{h.election_name ?? ""}</td>
                <td style={td}>{h.election_date ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Relationships */}
      <section style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, padding: 12, margin: "12px 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Relationships</div>
          <button style={BTN_LIGHT} onClick={() => setShowAddRel(true)}>+ Add</button>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr><th style={th}>Person</th><th style={th}>Relationship</th><th style={th}>When</th><th style={th}>Note</th></tr></thead>
          <tbody>
            {rels.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: 8, color: "#777" }}>No relationships yet</td></tr>
            ) : rels.map((r) => (
              <tr key={r.id}>
                <td style={td}>{r.person}</td>
                <td style={td}>{r.rel}</td>
                <td style={td}>{r.when}</td>
                <td style={td}>{r.note ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Edit Contact Info modal */}
      {showEdit && (
        <div style={modalBackdrop}>
          <div style={modalCard}>
            <div style={{ fontWeight: 800, marginBottom: 10 }}>Edit Contact Info</div>

            <LabeledInput label="Home phone" value={form.home} onChange={(v) => setForm((f) => ({ ...f, home: v }))} placeholder="e.g. 956-555-4444" />
            <LabeledInput label="Cell phone" value={form.cell} onChange={(v) => setForm((f) => ({ ...f, cell: v }))} placeholder="e.g. 956-555-1111" />
            <LabeledInput label="Email" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} placeholder="name@example.com" />
            <LabeledInput label="Facebook" value={form.facebook} onChange={(v) => setForm((f) => ({ ...f, facebook: v }))} placeholder="handle or URL" />
            <LabeledInput label="X / Twitter" value={form.x} onChange={(v) => setForm((f) => ({ ...f, x: v }))} placeholder="@handle or URL" />
            <LabeledInput label="Instagram" value={form.instagram} onChange={(v) => setForm((f) => ({ ...f, instagram: v }))} placeholder="@handle or URL" />
            <LabeledInput label="TikTok" value={form.tiktok} onChange={(v) => setForm((f) => ({ ...f, tiktok: v }))} placeholder="@handle or URL" />

            {(msg || err) && <div style={{ marginTop: 8, color: err ? "#a40000" : "#065F46" }}>{err ?? msg}</div>}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <button style={BTN_LIGHT} onClick={() => setShowEdit(false)} disabled={saving}>Cancel</button>
              <button style={BTN} onClick={saveContactInfo} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ====== NEW: Add Note modal ====== */}
      {showAddNote && (
        <div style={modalBackdrop}>
          <div style={modalCard}>
            <div style={{ fontWeight: 800, marginBottom: 10 }}>Add Note</div>
            <LabeledInput label="Type (optional)" value={noteForm.note_type} onChange={(v) => setNoteForm(f => ({ ...f, note_type: v }))} placeholder="e.g. Door Knock, Phone, General" />
            <label style={{ display: "grid", gap: 6, marginBottom: 10 }}>
              <span style={{ fontSize: 13, color: "#374151" }}>Note</span>
              <textarea
                value={noteForm.note}
                onChange={(e) => setNoteForm(f => ({ ...f, note: e.target.value }))}
                rows={5}
                style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #e5e7eb" }}
              />
            </label>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <button style={BTN_LIGHT} onClick={() => setShowAddNote(false)} disabled={saving}>Cancel</button>
              <button style={BTN} onClick={handleAddNote} disabled={saving || !noteForm.note.trim()}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== NEW: Add Contact modal ====== */}
      {showAddContact && (
        <div style={modalBackdrop}>
          <div style={modalCard}>
            <div style={{ fontWeight: 800, marginBottom: 10 }}>Add Contact</div>
            <LabeledInput label="When (YYYY-MM-DDTHH:mm)" value={contactForm.contacted_at} onChange={(v) => setContactForm(f => ({ ...f, contacted_at: v }))} />
            <LabeledInput label="Method" value={contactForm.contact_method} onChange={(v) => setContactForm(f => ({ ...f, contact_method: v }))} placeholder="e.g. Door, Phone, SMS" />
            <LabeledInput label="Type (optional)" value={contactForm.contact_type} onChange={(v) => setContactForm(f => ({ ...f, contact_type: v }))} placeholder="e.g. GOTV, Persuasion" />
            <LabeledInput label="Result (optional)" value={contactForm.result} onChange={(v) => setContactForm(f => ({ ...f, result: v }))} placeholder="e.g. Contacted, Left VM" />
            <label style={{ display: "grid", gap: 6, marginBottom: 10 }}>
              <span style={{ fontSize: 13, color: "#374151" }}>Notes (optional)</span>
              <textarea
                value={contactForm.notes}
                onChange={(e) => setContactForm(f => ({ ...f, notes: e.target.value }))}
                rows={4}
                style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #e5e7eb" }}
              />
            </label>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <button style={BTN_LIGHT} onClick={() => setShowAddContact(false)} disabled={saving}>Cancel</button>
              <button style={BTN} onClick={handleAddContact} disabled={saving || !contactForm.contact_method.trim()}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== NEW: Add Relationship modal ====== */}
      {showAddRel && (
        <div style={modalBackdrop}>
          <div style={modalCard}>
            <div style={{ fontWeight: 800, marginBottom: 10 }}>Add Relationship</div>
            <label style={{ display: "grid", gap: 6, marginBottom: 10 }}>
              <span style={{ fontSize: 13, color: "#374151" }}>Relationship Type</span>
              <select
                value={relForm.rel_type_id}
                onChange={(e) => setRelForm(f => ({ ...f, rel_type_id: e.target.value }))}
                style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #e5e7eb" }}
              >
                <option value="">Select…</option>
                {relTypes.map(rt => (
                  <option key={rt.id} value={rt.id}>{rt.label}</option>
                ))}
              </select>
            </label>
            <LabeledInput
              label="Other Person (campaign_voter_id)"
              value={relForm.to_id}
              onChange={(v) => setRelForm(f => ({ ...f, to_id: v }))}
              placeholder="Paste campaign_voter_id of the other person"
            />
            <label style={{ display: "grid", gap: 6, marginBottom: 10 }}>
              <span style={{ fontSize: 13, color: "#374151" }}>Notes (optional)</span>
              <textarea
                value={relForm.notes}
                onChange={(e) => setRelForm(f => ({ ...f, notes: e.target.value }))}
                rows={3}
                style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #e5e7eb" }}
              />
            </label>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
              <button style={BTN_LIGHT} onClick={() => setShowAddRel(false)} disabled={saving}>Cancel</button>
              <button style={BTN} onClick={handleAddRelationship} disabled={saving || !relForm.rel_type_id || !relForm.to_id}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LabeledInput(props: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <label style={{ display: "grid", gap: 6, marginBottom: 10 }}>
      <span style={{ fontSize: 13, color: "#374151" }}>{props.label}</span>
      <input
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid #e5e7eb" }}
      />
    </label>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
  display: "grid", placeItems: "center", zIndex: 50,
};
const modalCard: React.CSSProperties = {
  width: "min(680px, 92vw)", background: "#fff",
  borderRadius: 14, border: "1px solid #e5e7eb", padding: 16,
};
